import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  DollarSign, TrendingUp, Users, Target, FileText, ArrowRight, RefreshCw,
  Loader2, CheckCircle2, Clock, AlertTriangle, BarChart3, PieChart,
  UserCheck, HandshakeIcon, ShoppingCart, Package, Workflow, Plus
} from 'lucide-react';
import { dealsAPI, salesQuotesAPI, salesWorkflowAPI, prospectsAPI } from '../api';

const dealStageColors = {
  lead: 'bg-slate-100 text-slate-800',
  qualified: 'bg-blue-100 text-blue-800',
  proposal: 'bg-purple-100 text-purple-800',
  negotiation: 'bg-orange-100 text-orange-800',
  closed_won: 'bg-green-100 text-green-800',
  closed_lost: 'bg-red-100 text-red-800'
};

export const SalesDashboardPage = () => {
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data state
  const [deals, setDeals] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [quotesDashboard, setQuotesDashboard] = useState(null);
  const [workflowDashboard, setWorkflowDashboard] = useState(null);
  
  // Calculated stats
  const [stats, setStats] = useState({
    pipelineValue: 0,
    wonValue: 0,
    avgDealSize: 0,
    winRate: 0,
    prospectsCount: 0,
    openDeals: 0,
    pendingApprovals: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      const [dealsRes, prospectsRes, quotesRes, workflowRes] = await Promise.all([
        dealsAPI.getAll({ limit: 500 }),
        prospectsAPI.getAll({ limit: 100 }),
        salesQuotesAPI.getDashboard().catch(() => ({ data: null })),
        salesWorkflowAPI.getDashboard().catch(() => ({ data: null }))
      ]);
      
      const dealsList = dealsRes.data?.deals || dealsRes.data || [];
      setDeals(dealsList);
      
      const prospectsList = prospectsRes.data?.leads || prospectsRes.data || [];
      setProspects(prospectsList);
      
      setQuotesDashboard(quotesRes.data);
      setWorkflowDashboard(workflowRes.data);
      
      // Calculate stats
      const openDeals = dealsList.filter(d => !['closed_won', 'closed_lost'].includes(d.stage));
      const wonDeals = dealsList.filter(d => d.stage === 'closed_won');
      const closedDeals = dealsList.filter(d => ['closed_won', 'closed_lost'].includes(d.stage));
      
      const pipelineValue = openDeals.reduce((sum, d) => sum + (d.value || 0), 0);
      const wonValue = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);
      const avgDealSize = wonDeals.length > 0 ? wonValue / wonDeals.length : 0;
      const winRate = closedDeals.length > 0 ? (wonDeals.length / closedDeals.length) * 100 : 0;
      
      setStats({
        pipelineValue,
        wonValue,
        avgDealSize,
        winRate,
        prospectsCount: prospectsList.filter(p => !['converted', 'lost'].includes(p.status)).length,
        openDeals: openDeals.length,
        pendingApprovals: workflowRes.data?.approvals?.pending || 0
      });
      
    } catch (error) {
      console.error('Failed to fetch sales data:', error);
      toast.error('Failed to load sales dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Pipeline breakdown
  const pipelineBreakdown = {
    lead: deals.filter(d => d.stage === 'lead').length,
    qualified: deals.filter(d => d.stage === 'qualified').length,
    proposal: deals.filter(d => d.stage === 'proposal').length,
    negotiation: deals.filter(d => d.stage === 'negotiation').length,
    closed_won: deals.filter(d => d.stage === 'closed_won').length,
    closed_lost: deals.filter(d => d.stage === 'closed_lost').length
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Sales Dashboard
          </h1>
          <p className="text-muted-foreground">
            Track prospects, deals, quotes, and sales performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllData} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <Link to="/sales/prospects">
            <Button variant="outline" size="sm">
              <UserCheck className="h-4 w-4 mr-2" />
              Prospects
            </Button>
          </Link>
          <Link to="/sales/deals">
            <Button data-testid="new-deal-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Deal
            </Button>
          </Link>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold">${stats.pipelineValue.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Won Value</p>
                <p className="text-2xl font-bold text-green-600">${stats.wonValue.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Win Rate</p>
                <p className="text-2xl font-bold">{stats.winRate.toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Deal Size</p>
                <p className="text-2xl font-bold">${Math.round(stats.avgDealSize).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Deals</p>
                <p className="text-2xl font-bold">{stats.openDeals}</p>
              </div>
              <div className="p-3 bg-cyan-100 rounded-lg">
                <HandshakeIcon className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Prospects</p>
                <p className="text-2xl font-bold">{stats.prospectsCount}</p>
              </div>
              <div className="p-3 bg-rose-100 rounded-lg">
                <UserCheck className="h-5 w-5 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approvals</p>
                <p className="text-2xl font-bold text-orange-600">{stats.pendingApprovals}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Pipeline Overview
            </CardTitle>
            <CardDescription>Deals by stage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(pipelineBreakdown).map(([stage, count]) => {
              const total = deals.length;
              const percentage = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={stage}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm capitalize">{stage.replace('_', ' ')}</span>
                    <Badge className={dealStageColors[stage]}>{count}</Badge>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
            
            <Link to="/sales/pipeline" className="block mt-4">
              <Button variant="outline" size="sm" className="w-full">
                View Pipeline
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Prospects */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Recent Prospects
            </CardTitle>
            <CardDescription>New prospects from marketing</CardDescription>
          </CardHeader>
          <CardContent>
            {prospects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <UserCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No active prospects</p>
              </div>
            ) : (
              <ScrollArea className="h-[220px]">
                <div className="space-y-3">
                  {prospects.slice(0, 5).map((prospect) => (
                    <Link 
                      key={prospect.id} 
                      to={`/sales/prospects/${prospect.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div>
                          <p className="font-medium">{prospect.first_name} {prospect.last_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {prospect.company_name || prospect.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {prospect.status}
                          </Badge>
                          {prospect.source_type && (
                            <Badge variant="secondary" className="text-xs">
                              {prospect.source_type.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            )}
            
            <Link to="/sales/prospects" className="block mt-4">
              <Button variant="outline" size="sm" className="w-full">
                View All Prospects
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Quotes Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Quotes Summary
            </CardTitle>
            <CardDescription>Sales quote status</CardDescription>
          </CardHeader>
          <CardContent>
            {quotesDashboard?.stats ? (
              <>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Draft</span>
                    <Badge variant="secondary">{quotesDashboard.stats.draft}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Sent</span>
                    <Badge className="bg-blue-100 text-blue-800">{quotesDashboard.stats.sent}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Accepted</span>
                    <Badge className="bg-green-100 text-green-800">{quotesDashboard.stats.accepted}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total Value</span>
                    <span className="font-medium">${quotesDashboard.stats.total_accepted_value?.toLocaleString() || 0}</span>
                  </div>
                </div>
                
                {quotesDashboard.stats.expiring_soon > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      {quotesDashboard.stats.expiring_soon} quotes expiring soon
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No quotes yet</p>
              </div>
            )}
            
            <Link to="/sales/quotes" className="block">
              <Button variant="outline" size="sm" className="w-full">
                View All Quotes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Deals & Workflow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Deals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Deals</CardTitle>
              <CardDescription>Latest deal activity</CardDescription>
            </div>
            <Link to="/sales/deals">
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <HandshakeIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No deals yet</p>
                <Link to="/sales/deals">
                  <Button variant="outline" size="sm" className="mt-2">
                    Create First Deal
                  </Button>
                </Link>
              </div>
            ) : (
              <ScrollArea className="h-[250px]">
                <div className="space-y-3">
                  {deals.slice(0, 6).map((deal) => (
                    <Link 
                      key={deal.id} 
                      to={`/sales/deals/${deal.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <HandshakeIcon className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium">{deal.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {deal.company_name || deal.contact_name || 'No contact'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={dealStageColors[deal.stage]}>
                            {deal.stage?.replace('_', ' ')}
                          </Badge>
                          <span className="text-sm font-medium">${deal.value?.toLocaleString()}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Workflow & Approvals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Workflow & Approvals</CardTitle>
              <CardDescription>Sales approval requests</CardDescription>
            </div>
            <Link to="/sales/workflow-rules">
              <Button variant="outline" size="sm">
                Manage Rules
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {workflowDashboard ? (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold">{workflowDashboard.rules?.active || 0}</p>
                    <p className="text-xs text-muted-foreground">Active Rules</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{workflowDashboard.approvals?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending Approvals</p>
                  </div>
                </div>
                
                {workflowDashboard.recent_approvals?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Recent Approvals</p>
                    {workflowDashboard.recent_approvals.slice(0, 3).map((approval) => (
                      <div key={approval.id} className="p-2 border rounded text-sm">
                        <div className="flex items-center justify-between">
                          <span>{approval.entity_name}</span>
                          <Badge variant={approval.status === 'approved' ? 'default' : approval.status === 'pending' ? 'outline' : 'destructive'}>
                            {approval.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Workflow className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No workflow rules configured</p>
                <Link to="/sales/workflow-rules">
                  <Button variant="outline" size="sm" className="mt-2">
                    Setup Workflow Rules
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Link to="/sales/prospects">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <UserCheck className="h-8 w-8 mx-auto mb-2 text-rose-600" />
              <h3 className="font-semibold">Prospects</h3>
              <p className="text-sm text-muted-foreground">Manage leads</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/sales/deals">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <HandshakeIcon className="h-8 w-8 mx-auto mb-2 text-cyan-600" />
              <h3 className="font-semibold">Deals</h3>
              <p className="text-sm text-muted-foreground">Track opportunities</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/sales/quotes">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <h3 className="font-semibold">Quotes</h3>
              <p className="text-sm text-muted-foreground">Create proposals</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/sales/commerce">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <h3 className="font-semibold">Commerce</h3>
              <p className="text-sm text-muted-foreground">Products & services</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/sales/workflow-rules">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <Workflow className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <h3 className="font-semibold">Workflow Rules</h3>
              <p className="text-sm text-muted-foreground">Approval workflows</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default SalesDashboardPage;
